import type {ImageProcessingNodes as Nodes, PostProcessingInputData, PostProcessingSettings} from "@cm/image-processing-nodes"
import {postProcessingGraph} from "#image-processing/operations/render-post-processing"

type ImageNode = Nodes.ImageNode

export type LayerStackGroup<Option> = {options: {option: Option; input: PostProcessingInputData}[]}

const channel = (input: ImageNode, index: number): ImageNode => ({type: "extractChannel", input, channel: index})
const math = (firstInput: ImageNode, operation: Nodes.Math["operation"], secondInput: ImageNode | number): ImageNode => ({
    type: "math",
    firstInput,
    secondInput,
    operation,
})
const oneMinus = (input: ImageNode) => math(math(input, "*", -1), "+", 1)
const sum = (images: readonly ImageNode[]) => (images.length > 0 ? images.reduce((total, image) => math(total, "+", image)) : undefined)
const rgba = (channels: ImageNode[]): ImageNode => ({
    type: "combineChannels",
    channelLayout: "RGBA",
    input: channels as [ImageNode, ImageNode, ImageNode, ImageNode],
})
const asRgba = (input: ImageNode): ImageNode => ({type: "convert", input, channelLayout: "RGBA", dataType: "float32", sRGB: false})
const straight = (premultiplied: ImageNode, alpha: ImageNode) =>
    rgba([...[0, 1, 2].map((index) => math(channel(premultiplied, index), "/safe", channel(premultiplied, 3))), {type: "clamp", input: alpha}])

// Stacked bottom to top with normal blending, the images reproduce the post-processed full render: group k gets the alpha
// coverage_k / (1 - coverage of the groups above it), and the rest keeps the background only where no group covers the
// pixel. Coverage is geometric, so any option of a group gives it; transparent materials break that. Post-processing runs
// on each layer's own colour, so it matches the full render except at edges two layers share.
export function layerStackImages<Option>(rest: PostProcessingInputData, groups: readonly LayerStackGroup<Option>[], settings: PostProcessingSettings) {
    const coverage = (input: PostProcessingInputData) => channel(input.combinedPass, 3)
    const groupCoverages = groups.map((group) => coverage(group.options[0].input))
    const uncropped = {...settings, autoCrop: false}

    const region: Nodes.RegionNode | undefined =
        settings.autoCrop && settings.autoCropMargin
            ? {
                  type: "dilateRegion",
                  amount: settings.autoCropMargin,
                  region: {
                      type: "detectMaskRegion",
                      input: {type: "alphaToMask", input: sum([rest.combinedPass, ...groups.map((group) => group.options[0].input.combinedPass)])!},
                  },
              }
            : undefined
    const crop = (image: ImageNode): ImageNode => (region ? {type: "crop", region, input: image} : image)

    const full = asRgba(postProcessingGraph(rest, uncropped).image)
    const covered = sum(groupCoverages)
    let restImage: ImageNode
    if (covered) {
        const background = asRgba(postProcessingGraph({...rest, combinedPass: math(rest.combinedPass, "*", 0)}, uncropped).image)
        const remaining = [0, 1, 2, 3].map((index) => math(channel(full, index), "-", math(channel(background, index), "*", covered)))
        restImage = straight(rgba(remaining), math(remaining[3], "/safe", oneMinus(covered)))
    } else {
        restImage = straight(full, channel(full, 3))
    }

    const layers = groups.map((group, index) => {
        const above = sum(groupCoverages.slice(index + 1))
        return group.options.map(({option, input}) => {
            const image = asRgba(postProcessingGraph(input, {...uncropped, composite: false}).image)
            const alpha = above ? math(coverage(input), "/safe", oneMinus(above)) : coverage(input)
            return {option, image: crop(straight(image, alpha))}
        })
    })
    return {rest: crop(restImage), layers}
}
